// ═══════════════════════════════════════════════════════════════
//  Rolling Forecast
//  • 18-month rolling window (actuals + projections)
//  • Confidence band rendering
//  • Forecast accuracy tracking
//  • Reforecast entry form
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ComposedChart, Line, Area, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
  BarChart, Cell,
} from "recharts";
import LoaderComponent from "../../../../libraries/loader/loader";
import { PBExecuteQuery, PBCreateData, Q, userMeta, fmtDate } from "../../../../services/process/PlanningBudgeting/planningBudgetingService";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const YEARS  = [2023, 2024, 2025, 2026, 2027];

const FIELD_STYLE = {
  padding: "7px 10px", borderRadius: 7,
  border: "1px solid #d0d8ec", fontSize: 13,
  width: "100%", boxSizing: "border-box",
};
const LABEL_STYLE = {
  fontSize: 11, color: "#888", fontWeight: 600,
  display: "block", marginBottom: 3,
};

const SECTION = ({ title, children }) => (
  <div style={{
    background: "#fff", borderRadius: 14,
    boxShadow: "0 2px 14px rgba(73,105,171,0.09)",
    padding: "20px 22px", marginBottom: 22,
  }}>
    <div style={{ fontWeight: 700, fontSize: 15, color: "#1a2340", marginBottom: 16 }}>{title}</div>
    {children}
  </div>
);

const ForecastTypeTag = ({ type }) => {
  const colors = { Actuals: "#2d8c4e", Rolling: "#4969ab", Revised: "#e67e22", Budget: "#7b68ee" };
  const c = colors[type] || "#888";
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
      background: `${c}18`, color: c, textTransform: "uppercase", letterSpacing: 0.5,
    }}>{type}</span>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e0e6f0",
      borderRadius: 8, padding: "10px 16px",
      boxShadow: "0 4px 16px rgba(0,0,0,0.10)", fontSize: 12,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: "#1a2340" }}>{label}</div>
      {payload.filter(p => p.name !== "band").map((p) => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}:{" "}
          <strong>₹{Number(p.value).toLocaleString("en-IN")}</strong>
        </div>
      ))}
    </div>
  );
};

const DEFAULT_FORM = {
  forecast_month: "January", forecast_year: new Date().getFullYear(),
  revenue_forecast: 0, expense_forecast: 0, ebitda_forecast: 0,
  confidence_low: 0, confidence_high: 0, forecast_type: "Rolling",
};

export default function RollingForecast() {
  const [tab,      setTab]     = useState("chart");
  const [data,     setData]    = useState([]);
  const [form,     setForm]    = useState({ ...DEFAULT_FORM });
  const [loading,  setLoading] = useState(false);
  const [metric,   setMetric]  = useState("revenue"); // revenue | expense | ebitda

  const [userCredentials] = useState(
    JSON.parse(sessionStorage.getItem("userCredentials") || "{}")
  );

  const fetchForecast = useCallback(async () => {
    setLoading(true);
    try {
      const c = PBExecuteQuery();
      const res = await axios.post(c[1], { query: Q.rollingForecast(userCredentials.selectedentity) }, c[0]);
      setData(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [userCredentials.selectedentity]);

  useEffect(() => { fetchForecast(); }, [fetchForecast]);

  // ── Chart data ──────────────────────────────────────────────
  const chartData = data.map((r) => {
    const fKey     = `${metric}_forecast`;
    const aKey     = `actual_${metric === "ebitda" ? "revenue" : metric}`;
    const forecast = Number(r[fKey] || 0);
    const actual   = Number(r[aKey] || 0);
    const low      = Number(r.confidence_low  || forecast * 0.88);
    const high     = Number(r.confidence_high || forecast * 1.12);
    return {
      label:    `${r.forecast_month?.slice(0,3)} ${String(r.forecast_year).slice(2)}`,
      forecast,
      actual:   actual || null,
      band:     [low, high],
      type:     r.forecast_type,
    };
  });

  // ── Forecast accuracy ─────────────────────────────────────
  const accuracyData = data
    .filter((r) => r.actual_revenue && r.revenue_forecast)
    .map((r) => ({
      label:    `${r.forecast_month?.slice(0,3)} ${String(r.forecast_year).slice(2)}`,
      accuracy: Math.round(100 - Math.abs(((Number(r.actual_revenue) - Number(r.revenue_forecast)) / Number(r.revenue_forecast)) * 100)),
    }));

  // ── Derived totals ────────────────────────────────────────
  const totalForecastRev  = data.reduce((s,r) => s + Number(r.revenue_forecast  || 0), 0);
  const totalForecastExp  = data.reduce((s,r) => s + Number(r.expense_forecast  || 0), 0);
  const totalForecastEBIT = data.reduce((s,r) => s + Number(r.ebitda_forecast   || 0), 0);
  const avgAccuracy       = accuracyData.length
    ? (accuracyData.reduce((s,r) => s + r.accuracy, 0) / accuracyData.length).toFixed(1)
    : "–";

  const handleField = (k, v) => {
    setForm((prev) => {
      const up = { ...prev, [k]: v };
      if (["revenue_forecast","expense_forecast"].includes(k)) {
        up.ebitda_forecast    = Number(up.revenue_forecast) - Number(up.expense_forecast);
        up.confidence_low     = up.ebitda_forecast * 0.88;
        up.confidence_high    = up.ebitda_forecast * 1.12;
      }
      return up;
    });
  };

  const handleSave = async () => {
    const payload = {
      database: "planning_budgeting",
      table: "rolling_forecast",
      formData: {
        ...form,
        month_num: MONTHS.indexOf(form.forecast_month) + 1,
        ...userMeta(userCredentials),
      },
    };
    try {
      const c = PBCreateData();
      await axios.post(c[1], payload, c[0]);
      alert("Forecast entry saved!");
      setForm({ ...DEFAULT_FORM });
      fetchForecast();
      setTab("chart");
    } catch {
      alert("Error saving forecast");
    }
  };

  const currentMonth = new Date().toLocaleString("default", { month: "short" });

  if (loading) return <LoaderComponent loaderOpen />;

  return (
    <div style={{ padding: "4px 2px" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#1a2340", marginBottom: 4 }}>Rolling Forecast</div>
      <div style={{ fontSize: 12, color: "#aaa", marginBottom: 18 }}>
        18-month rolling window · actuals + forward projections with confidence intervals
      </div>

      {/* ── Summary pills ── */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {[
          { label: "Total Forecast Revenue",  value: `₹${(totalForecastRev/10000000).toFixed(2)}Cr`,  color: "#4969ab" },
          { label: "Total Forecast Expense",  value: `₹${(totalForecastExp/10000000).toFixed(2)}Cr`,  color: "#c0392b" },
          { label: "Forecast EBITDA",         value: `₹${(totalForecastEBIT/10000000).toFixed(2)}Cr`, color: "#2d8c4e" },
          { label: "Forecast Accuracy",       value: `${avgAccuracy}%`,                               color: "#e67e22" },
          { label: "Data Points",             value: data.length,                                      color: "#7b68ee" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: `${color}10`, border: `1px solid ${color}30`,
            borderRadius: 10, padding: "10px 16px", minWidth: 140,
          }}>
            <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
            <div style={{ fontWeight: 800, fontSize: 18, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 0, marginBottom: 22, borderBottom: "2px solid #e8ecf4" }}>
        {[
          ["chart",    "📈 Forecast Chart"],
          ["accuracy", "🎯 Accuracy Tracking"],
          ["entry",    "➕ Add Forecast Entry"],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: "8px 20px", border: "none", cursor: "pointer",
            background: tab === k ? "#4969ab" : "transparent",
            color: tab === k ? "#fff" : "#666",
            fontWeight: tab === k ? 700 : 400,
            fontSize: 13, borderRadius: "8px 8px 0 0", transition: "all 0.15s",
          }}>{lbl}</button>
        ))}
      </div>

      {/* ══ TAB: Chart ══ */}
      {tab === "chart" && (
        <div>
          {/* Metric switcher */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[["revenue","Revenue"],["expense","Expense"],["ebitda","EBITDA"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setMetric(k)} style={{
                padding: "5px 16px", borderRadius: 20, border: "1px solid",
                borderColor: metric === k ? "#4969ab" : "#d0d8ec",
                background: metric === k ? "#4969ab" : "#fff",
                color: metric === k ? "#fff" : "#666",
                fontSize: 12, fontWeight: metric === k ? 700 : 400, cursor: "pointer",
              }}>{lbl}</button>
            ))}
          </div>

          <SECTION title={`📈 Rolling Forecast — ${metric.charAt(0).toUpperCase() + metric.slice(1)}`}>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={340}>
                <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine x={`${currentMonth} ${String(new Date().getFullYear()).slice(2)}`}
                    stroke="#4969ab" strokeDasharray="5 3"
                    label={{ value: "Now", position: "top", fontSize: 10, fill: "#4969ab" }} />
                  <Area type="monotone" dataKey="band" fill="#4969ab18" stroke="none" name="band" />
                  <Line type="monotone" dataKey="forecast" stroke="#4969ab" strokeWidth={2.5}
                    strokeDasharray="6 3" dot={{ r: 3 }} name="Forecast" />
                  <Line type="monotone" dataKey="actual" stroke="#2d8c4e" strokeWidth={2.5}
                    dot={{ r: 4 }} name="Actual" connectNulls={false} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: "center", color: "#bbb", padding: 60 }}>
                No forecast data yet. Add entries to get started.
              </div>
            )}
          </SECTION>

          {/* Data table */}
          {data.length > 0 && (
            <SECTION title="📋 Forecast Data">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f5f7fb" }}>
                      {["Period","Type","Rev Forecast","Exp Forecast","EBITDA Forecast","CI Low","CI High","Actual Rev"].map(h => (
                        <th key={h} style={{ padding: "8px 10px", textAlign: h==="Period"||h==="Type"?"left":"right", color:"#888", fontWeight:600, fontSize:11, whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((r, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f0f2f8" }}>
                        <td style={{ padding: "7px 10px", fontWeight: 600, whiteSpace: "nowrap" }}>
                          {r.forecast_month?.slice(0,3)} {r.forecast_year}
                        </td>
                        <td style={{ padding: "7px 10px" }}><ForecastTypeTag type={r.forecast_type} /></td>
                        {["revenue_forecast","expense_forecast","ebitda_forecast","confidence_low","confidence_high","actual_revenue"].map((k) => (
                          <td key={k} style={{
                            padding: "7px 10px", textAlign: "right",
                            color: k === "actual_revenue" ? "#2d8c4e" : k === "ebitda_forecast" ? "#4969ab" : "#333",
                            fontWeight: ["ebitda_forecast","actual_revenue"].includes(k) ? 700 : 400,
                          }}>
                            {r[k] ? `₹${Number(r[k]).toLocaleString("en-IN")}` : "–"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SECTION>
          )}
        </div>
      )}

      {/* ══ TAB: Accuracy ══ */}
      {tab === "accuracy" && (
        <SECTION title="🎯 Forecast Accuracy vs Actuals">
          {accuracyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={accuracyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${v}%`} />
                <ReferenceLine y={90} stroke="#2d8c4e" strokeDasharray="4 3"
                  label={{ value: "90% target", position: "right", fontSize: 10, fill: "#2d8c4e" }} />
                <Bar dataKey="accuracy" name="Accuracy %" radius={[4,4,0,0]}>
                  {accuracyData.map((r, i) => (
                    <Cell key={i} fill={r.accuracy >= 90 ? "#2d8c4e" : r.accuracy >= 75 ? "#e67e22" : "#c0392b"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", color: "#bbb", padding: 60 }}>
              No actuals available yet to measure accuracy.
            </div>
          )}
        </SECTION>
      )}

      {/* ══ TAB: Entry ══ */}
      {tab === "entry" && (
        <div style={{ maxWidth: 600 }}>
          <SECTION title="➕ Add / Update Forecast Entry">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={LABEL_STYLE}>Forecast Month</label>
                <select value={form.forecast_month} onChange={(e) => handleField("forecast_month", e.target.value)} style={FIELD_STYLE}>
                  {MONTHS.map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={LABEL_STYLE}>Forecast Year</label>
                <select value={form.forecast_year} onChange={(e) => handleField("forecast_year", Number(e.target.value))} style={FIELD_STYLE}>
                  {YEARS.map((y) => <option key={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label style={LABEL_STYLE}>Forecast Type</label>
                <select value={form.forecast_type} onChange={(e) => handleField("forecast_type", e.target.value)} style={FIELD_STYLE}>
                  {["Rolling","Revised","Budget","Actuals"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              {[
                { key: "revenue_forecast",  label: "Revenue Forecast (₹)" },
                { key: "expense_forecast",  label: "Expense Forecast (₹)" },
                { key: "ebitda_forecast",   label: "EBITDA Forecast (₹)",  disabled: true },
                { key: "confidence_low",    label: "Confidence Low (₹)",   disabled: true },
                { key: "confidence_high",   label: "Confidence High (₹)",  disabled: true },
              ].map(({ key, label, disabled }) => (
                <div key={key} style={{ gridColumn: "span 2" }}>
                  <label style={LABEL_STYLE}>{label}</label>
                  <input
                    type="number" value={form[key]}
                    disabled={disabled}
                    onChange={(e) => handleField(key, Number(e.target.value))}
                    style={{ ...FIELD_STYLE, background: disabled ? "#f5f7fb" : "#fff", color: disabled ? "#aaa" : "#333" }}
                  />
                </div>
              ))}
            </div>

            {/* Live EBITDA preview */}
            <div style={{
              marginTop: 16, background: "linear-gradient(135deg,#4969ab,#7b68ee)",
              borderRadius: 10, padding: "14px 18px", color: "#fff",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>EBITDA Forecast</div>
                <div style={{ fontSize: 22, fontWeight: 900 }}>
                  ₹{Number(form.ebitda_forecast).toLocaleString("en-IN")}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, opacity: 0.6 }}>Confidence Range</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  ₹{Number(form.confidence_low).toLocaleString("en-IN")} – ₹{Number(form.confidence_high).toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            <button onClick={handleSave} style={{
              marginTop: 18, padding: "9px 28px", background: "#4969ab", color: "#fff",
              border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 14,
            }}>Save Forecast Entry</button>
          </SECTION>
        </div>
      )}
    </div>
  );
}
